import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { MobileUnsupported } from './components/MobileUnsupported';
import { SubscriptionDashboard } from './pages/SubscriptionDashboard';
import { SubscriptionDetailPage } from './pages/SubscriptionDetailPage';
import { WineClubDashboard } from './pages/WineClubDashboard';
import { OrdersPage } from './pages/OrdersPage';
import { TagManagementPage } from './pages/TagManagementPage';
import { SettingsGeneralPage } from './pages/SettingsGeneralPage';
import { SettingsOrderProcessingPage } from './pages/SettingsOrderProcessingPage';
import { SettingsCustomerPortalPage } from './pages/SettingsCustomerPortalPage';
import { SettingsAccountBillingPage } from './pages/SettingsAccountBillingPage';
import { CancellationFlowPage } from './pages/CancellationFlowPage';
import { CancellationSettingsPage } from './pages/CancellationSettingsPage';
import { BoxBuilderPage } from './pages/BoxBuilderPage';
import { ContactsPage } from './pages/ContactsPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { CohortRetentionPage } from './pages/CohortRetentionPage';
import { ProductsPage } from './pages/ProductsPage';
import { AIChatPage } from './pages/AIChatPage';
import { PaymentRecoveryPage } from './pages/PaymentRecoveryPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ProductReplacePage } from './pages/ProductReplacePage';
import { OperationsActivityLogPage } from './pages/OperationsActivityLogPage';
import { TasksPage } from './pages/TasksPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { ReportsPage } from './pages/ReportsPage';
import { LoyaltyOverviewPage } from './pages/LoyaltyOverviewPage';
import { LoyaltyMembersPage } from './pages/LoyaltyMembersPage';
import { LoyaltyRewardsPage } from './pages/LoyaltyRewardsPage';
import { LoyaltyRulesPage } from './pages/LoyaltyRulesPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AllocationsPage } from './pages/AllocationsPage';
import { CreateAllocationPage } from './pages/CreateAllocationPage';
import { AllocationDetailPage } from './pages/AllocationDetailPage';
import { AllocationTemplatesPage } from './pages/AllocationTemplatesPage';
import { MigrationHubPage } from './pages/MigrationHubPage';
import { MigrationWizardPage } from './pages/MigrationWizardPage';
import { MigrationDetailPage } from './pages/MigrationDetailPage';
import { AutomationRulesPage } from './pages/AutomationRulesPage';
import { CreateAutomationRulePage } from './pages/CreateAutomationRulePage';
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  // Scroll to top on route change
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);
  // Determine current page from route
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path === '/dashboard/retention') return 'Cohort Retention';
    if (path === '/subscriptions') return 'Subscriptions';
    if (path === '/analytics') return 'Analytics';
    if (path === '/orders') return 'Orders';
    if (path === '/allocations') return 'All Allocations';
    if (path === '/allocations/templates') return 'Templates';
    if (path === '/ai-chat') return 'AI Chat';
    if (path === '/club-builder') return 'Club Builder';
    if (path === '/products') return 'Products';
    if (path === '/contacts') return 'Contacts';
    if (path.startsWith('/contacts/')) return 'Contacts';
    if (path === '/integrations') return 'Integrations';
    if (path === '/loyalty/members') return 'Loyalty Members';
    if (path === '/loyalty/rewards') return 'Loyalty Rewards';
    if (path === '/loyalty/rules') return 'Rules & Tiers';
    if (path === '/loyalty') return 'Loyalty Overview';
    if (path === '/settings' || path === '/settings/general') return 'General';
    if (path === '/settings/tags') return 'Member Tags';
    if (path === '/settings/automation-rules/create') return 'Automation Rules';
    if (path.startsWith('/settings/automation-rules/')) return 'Automation Rules';
    if (path === '/settings/automation-rules') return 'Automation Rules';
    if (path === '/settings/order-processing') return 'Order Processing';
    if (path === '/settings/customer-portal') return 'Customer Portal';
    if (path === '/settings/account-billing') return 'Account & Billing';
    if (path === '/management/cancellation-flow') return 'Cancellation Flow';
    if (path === '/management/tools/cancellation-settings') return 'Cancellation Tools';
    if (path === '/management/payment-recovery') return 'Payment Recovery';
    if (path === '/management/notifications') return 'Notifications';
    if (path === '/management/migration') return 'Migration';
    if (path === '/management/migration/new') return 'Migration';
    if (path.startsWith('/management/migration/')) return 'Migration';
    if (path === '/operations/product-replace') return 'Product Replace';
    if (path === '/operations/activity-log') return 'Activity Log';
    if (path === '/operations/tasks') return 'Tasks';
    if (path === '/operations/reports') return 'Reports';
    // Check for subscription detail route
    if (path.startsWith('/subscriptions/')) return 'Subscriptions';
    return 'Dashboard';
  };
  // Handle navigation from Sidebar
  const handleNavigate = (page: string) => {
    const routeMap: Record<string, string> = {
      Dashboard: '/',
      'Cohort Retention': '/dashboard/retention',
      Analytics: '/analytics',
      Subscriptions: '/subscriptions',
      Orders: '/orders',
      Contacts: '/contacts',
      Contact: '/contacts',
      'AI Chat': '/ai-chat',
      Chat: '/ai-chat',
      'Club Builder': '/club-builder',
      Products: '/products',
      Integrations: '/integrations',
      'Loyalty Overview': '/loyalty',
      'Loyalty Members': '/loyalty/members',
      'Loyalty Rewards': '/loyalty/rewards',
      'Rules & Tiers': '/loyalty/rules',
      'All Allocations': '/allocations',
      Templates: '/allocations/templates',
      Settings: '/settings/general',
      General: '/settings/general',
      'Order Processing': '/settings/order-processing',
      'Customer Portal': '/settings/customer-portal',
      'Account & Billing': '/settings/account-billing',
      'Account Settings': '/settings/account-billing',
      'Tag Management': '/settings/tags',
      Tags: '/settings/tags',
      'Member Tags': '/settings/tags',
      'Automation Rules': '/settings/automation-rules',
      'Cancellation Flow': '/management/cancellation-flow',
      'Cancellation Tools': '/management/tools/cancellation-settings',
      'Cancellation Settings': '/management/tools/cancellation-settings',
      'Payment Recovery': '/management/payment-recovery',
      Notifications: '/management/notifications',
      Migration: '/management/migration',
      'Product Replace': '/operations/product-replace',
      'Activity Log': '/operations/activity-log',
      'Operations Activity Log': '/operations/activity-log',
      Tasks: '/operations/tasks',
      Reports: '/operations/reports'
    };
    const route = routeMap[page] || '/';
    navigate(route);
  };
  const currentPage = getCurrentPage();
  return <>
      {/* Mobile Unsupported Overlay - Shows on mobile, hides everything else */}
      <MobileUnsupported data-id="element-5" />

      {/* Main App Layout - Hidden on mobile, visible on tablet and desktop */}
      <div className="hidden md:flex h-screen w-full bg-[#F1F5F9]" data-id="element-6">
        {/* Sidebar - only visible on md and up */}
        <Sidebar currentPage={currentPage} onNavigate={handleNavigate} data-id="element-7" />

        {/* Page content */}
        <div ref={contentRef} className="flex-1 overflow-auto min-h-screen" data-id="element-8">
          <Routes data-id="element-9">
            <Route path="/" element={<WineClubDashboard data-id="element-11" />} data-id="element-10" />
            <Route path="/dashboard/retention" element={<CohortRetentionPage data-id="element-13" />} data-id="element-12" />

            <Route path="/analytics" element={<AnalyticsPage data-id="element-15" />} data-id="element-14" />
            <Route path="/subscriptions" element={<SubscriptionDashboard data-id="element-17" />} data-id="element-16" />
            <Route path="/subscriptions/:id" element={<SubscriptionDetailPage data-id="element-19" />} data-id="element-18" />

            <Route path="/orders" element={<OrdersPage data-id="element-21" />} data-id="element-20" />
            <Route path="/products" element={<ProductsPage data-id="element-23" />} data-id="element-22" />
            <Route path="/contacts" element={<ContactsPage data-id="element-25" />} data-id="element-24" />
            <Route path="/contacts/:id" element={<ContactDetailPage data-id="element-27" />} data-id="element-26" />
            <Route path="/ai-chat" element={<AIChatPage data-id="element-29" />} data-id="element-28" />
            <Route path="/club-builder" element={<BoxBuilderPage data-id="element-31" />} data-id="element-30" />
            <Route path="/integrations" element={<IntegrationsPage data-id="element-33" />} data-id="element-32" />
            <Route path="/loyalty" element={<LoyaltyOverviewPage data-id="element-35" />} data-id="element-34" />
            <Route path="/loyalty/members" element={<LoyaltyMembersPage data-id="element-37" />} data-id="element-36" />
            <Route path="/loyalty/rewards" element={<LoyaltyRewardsPage data-id="element-39" />} data-id="element-38" />
            <Route path="/loyalty/rules" element={<LoyaltyRulesPage data-id="element-41" />} data-id="element-40" />
            <Route path="/allocations/create" element={<CreateAllocationPage data-id="element-43" />} data-id="element-42" />

            <Route path="/allocations/:id/edit" element={<CreateAllocationPage data-id="element-45" />} data-id="element-44" />

            <Route path="/allocations/templates" element={<AllocationTemplatesPage data-id="element-47" />} data-id="element-46" />

            <Route path="/allocations/:id" element={<AllocationDetailPage data-id="element-49" />} data-id="element-48" />
            <Route path="/allocations" element={<AllocationsPage data-id="element-51" />} data-id="element-50" />
            <Route path="/settings" element={<SettingsGeneralPage data-id="element-53" />} data-id="element-52" />
            <Route path="/settings/general" element={<SettingsGeneralPage data-id="element-55" />} data-id="element-54" />
            <Route path="/settings/order-processing" element={<SettingsOrderProcessingPage data-id="element-57" />} data-id="element-56" />

            <Route path="/settings/customer-portal" element={<SettingsCustomerPortalPage data-id="element-59" />} data-id="element-58" />

            <Route path="/settings/account-billing" element={<SettingsAccountBillingPage data-id="element-61" />} data-id="element-60" />

            <Route path="/settings/tags" element={<TagManagementPage data-id="element-63" />} data-id="element-62" />
            <Route path="/settings/automation-rules/create" element={<CreateAutomationRulePage data-id="element-65" />} data-id="element-64" />

            <Route path="/settings/automation-rules/:id" element={<CreateAutomationRulePage data-id="element-67" />} data-id="element-66" />

            <Route path="/settings/automation-rules" element={<AutomationRulesPage data-id="element-69" />} data-id="element-68" />

            <Route path="/management/cancellation-flow" element={<CancellationFlowPage data-id="element-71" />} data-id="element-70" />

            <Route path="/management/tools/cancellation-settings" element={<CancellationSettingsPage data-id="element-73" />} data-id="element-72" />

            <Route path="/management/payment-recovery" element={<PaymentRecoveryPage data-id="element-75" />} data-id="element-74" />

            <Route path="/management/notifications" element={<NotificationsPage data-id="element-77" />} data-id="element-76" />

            <Route path="/management/migration" element={<MigrationHubPage data-id="element-79" />} data-id="element-78" />

            <Route path="/management/migration/new" element={<MigrationWizardPage data-id="element-81" />} data-id="element-80" />

            <Route path="/management/migration/:id" element={<MigrationDetailPage data-id="element-83" />} data-id="element-82" />

            <Route path="/operations/product-replace" element={<ProductReplacePage data-id="element-85" />} data-id="element-84" />

            <Route path="/operations/activity-log" element={<OperationsActivityLogPage data-id="element-87" />} data-id="element-86" />

            <Route path="/operations/tasks" element={<TasksPage data-id="element-89" />} data-id="element-88" />
            <Route path="/operations/reports" element={<ReportsPage data-id="element-91" />} data-id="element-90" />
          </Routes>
        </div>
      </div>
    </>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-92">
      <AppContent data-id="element-93" />
    </BrowserRouter>;
}